import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { api } from "@/lib/api";

export const Testimonials = () => {
  const { t, L } = useI18n();
  const { data } = useSite();
  const [google, setGoogle] = useState(null);
  const items = data?.testimonials || [];

  useEffect(() => {
    api.get("/google-reviews").then((r) => setGoogle(r.data)).catch(() => {});
  }, []);

  return (
    <section
      id="opiniones"
      data-testid="testimonials-section"
      className="py-24 sm:py-32 bg-panel/40 border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="font-mono text-[11px] tracking-[0.3em] text-gold mb-4">{t("reviews.eyebrow")}</p>
          <h2 className="font-display text-4xl sm:text-5xl text-bone leading-[0.95]">{t("reviews.title")}</h2>
        </div>

        {google?.rating && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="mt-12 max-w-3xl mx-auto border border-gold/20 bg-ink p-7 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6"
            data-testid="google-rating-badge"
          >
            <div className="flex items-center gap-6">
              <div className="font-display text-5xl text-gold leading-none">
                {Number(google.rating).toFixed(1)}
              </div>
              <div>
                <div className="flex gap-1 justify-center sm:justify-start">
                  {[...Array(Math.round(google.rating))].map((_, s) => (
                    <Star key={s} size={16} className="fill-gold text-gold" />
                  ))}
                </div>
                <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-mut mt-2 text-center sm:text-left">
                  {t("reviews.google_count").replace("{n}", google.count)}
                </p>
              </div>
            </div>
            <a
              data-testid="google-reviews-link"
              href={google.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-gold/40 text-gold text-sm font-semibold px-5 py-3 rounded-full hover:bg-gold hover:text-ink transition-colors whitespace-nowrap"
            >
              <ExternalLink size={15} />
              {t("reviews.google_cta")}
            </a>
          </motion.div>
        )}

        {items.length === 0 ? (
          <p className="mt-14 text-center text-mut font-quote italic text-xl" data-testid="reviews-empty-state">
            {t("reviews.empty")}
          </p>
        ) : (
          <div data-testid="testimonials-grid" className="mt-14 grid md:grid-cols-3 gap-5">
            {items.map((r, i) => (
              <motion.blockquote
                key={r.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="border border-gold/10 bg-ink p-8 flex flex-col gap-5 hover:border-gold/35 transition-colors"
                data-testid={`testimonial-card-${r.id}`}
              >
                <div className="flex gap-1">
                  {[...Array(r.rating || 5)].map((_, s) => (
                    <Star key={s} size={14} className="fill-gold text-gold" />
                  ))}
                </div>
                {L(r.text_es, r.text_en) ? (
                  <p className="font-quote italic text-xl text-bone/90 leading-snug">{L(r.text_es, r.text_en)}</p>
                ) : (
                  <div className="flex items-center gap-3 mt-1" data-testid={`google-review-verified-${r.id}`}>
                    <span className="h-8 w-8 rounded-full bg-gold text-ink font-display text-sm flex items-center justify-center shrink-0">
                      G
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-mut">
                      {L("Reseña verificada de Google", "Verified Google review")}
                    </span>
                  </div>
                )}
                <footer className="mt-auto font-mono text-[11px] tracking-[0.2em] uppercase text-gold/80">
                  — {r.name}
                </footer>
              </motion.blockquote>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
